import {
  Badge,
  Button,
  Dialog,
  Field,
  FileDrop,
  Input,
  MessageListDialog,
  MultiSelect,
  Select,
  toast,
} from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ASPECT_RATIO_STRATEGIES,
  FEED_TYPES,
  FEED_TYPE_LABELS,
  RATIOS,
  RATIO_VALUES,
  SHOPIFY_CATALOGS,
  SHOPIFY_CATALOG_LABELS,
  useApi,
  type AspectRatioStrategy,
  type FeedType,
  type ProductCategoryDTO,
  type Ratio,
  type ShopifyCatalog,
} from "../../api";

/** Shared shape of the Shopify action dialogs. */
export interface ShopifyChoice {
  storeId: number;
  catalog: ShopifyCatalog;
  categoryIds: number[];
}

/**
 * Store picker, optionally with the catalog and product-category pickers the
 * create / update-variants actions need.
 *
 * The category list drops GIANT-family wood categories, matching the Vaadin
 * form: those are made to order and never listed on Shopify. "MM" (Museum
 * Mount) stays selectable but is not preselected.
 */
export function ShopifyActionDialog({
  open,
  onClose,
  title,
  confirmLabel,
  withCatalog = false,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  confirmLabel: string;
  withCatalog?: boolean;
  onConfirm: (choice: ShopifyChoice) => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const [storeId, setStoreId] = useState("");
  const [catalog, setCatalog] = useState<ShopifyCatalog>("GIANT_ART");
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const stores = useQuery({
    queryKey: ["store"],
    queryFn: () => api.stores.findAll(),
    enabled: open,
  });

  const categories = useQuery({
    queryKey: ["product", "category", "open"],
    queryFn: () => api.productCategories.getWithOpenProducts(),
    enabled: open && withCatalog,
  });

  const eligible = useMemo(
    () =>
      (categories.data ?? []).filter(
        (entry) =>
          !(entry.family === "GIANT" && (entry.material ?? "").toLowerCase() === "wood"),
      ),
    [categories.data],
  );

  useEffect(() => {
    if (!open) return;
    const first = stores.data?.[0];
    if (first && !storeId) setStoreId(String(first.id));
  }, [open, stores.data, storeId]);

  useEffect(() => {
    if (!withCatalog || eligible.length === 0 || categoryIds.length > 0) return;
    setCategoryIds(
      eligible.filter((entry) => entry.code?.toUpperCase() !== "MM").map((entry) => String(entry.id)),
    );
  }, [categoryIds.length, eligible, withCatalog]);

  const store = (stores.data ?? []).find((entry) => String(entry.id) === storeId);
  // Only the giantart storefront carries more than one catalog.
  const catalogOptions =
    store?.code === "giantart"
      ? SHOPIFY_CATALOGS
      : (["GIANT_ART"] as ShopifyCatalog[]);

  const confirm = async () => {
    if (!storeId) return;
    setBusy(true);
    try {
      await onConfirm({
        storeId: Number(storeId),
        catalog,
        categoryIds: categoryIds.map(Number),
      });
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" loading={busy} disabled={!storeId} onClick={() => void confirm()}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label={t("form.field.shopify-store", "Shopify store")} required>
          <Select
            value={storeId}
            options={(stores.data ?? []).map((entry) => ({
              value: String(entry.id),
              label: entry.name,
            }))}
            placeholder={t("form.field.select", "Select")}
            onChange={(event) => setStoreId(event.target.value)}
          />
        </Field>

        {withCatalog && (
          <>
            <Field label={t("form.field.catalog", "Catalog")} required>
              <Select
                value={catalog}
                options={catalogOptions.map((entry) => ({
                  value: entry,
                  label: SHOPIFY_CATALOG_LABELS[entry],
                }))}
                onChange={(event) => setCatalog(event.target.value as ShopifyCatalog)}
              />
            </Field>
            <Field label={t("form.field.categories", "Categories")} required>
              <MultiSelect
                options={eligible.map((entry) => ({
                  value: String(entry.id),
                  label: entry.name,
                }))}
                value={categoryIds}
                onChange={setCategoryIds}
              />
            </Field>
          </>
        )}
      </div>
    </Dialog>
  );
}

/** Feed export: a retailer template plus the categories to expand into rows. */
/**
 * A category as the export pickers show it: its name, then what tells it from
 * its namesakes (Giantart on wood or aluminum, a frame's colour), then its
 * code: "Giantart · Wood (GW)", "Matted and framed · Black (MFB)".
 */
function categoryLabel(entry: ProductCategoryDTO): string {
  const details = [entry.material, entry.color].filter((value): value is string => Boolean(value?.trim()));
  // Only Giant art comes in two materials; elsewhere the material is the same for all, and the colour says more.
  const shown = entry.family === "GIANT" ? details : details.filter((value) => value !== entry.material);
  return `${entry.name}${shown.length > 0 ? ` · ${shown.join(", ")}` : ""} (${entry.code})`;
}

/** Ticked when an export opens: matted and framed, canvas framed, gallery wrap, Giant art on wood. */
function selectedByDefault(entry: ProductCategoryDTO): boolean {
  const name = entry.name.toLowerCase();
  if (entry.family === "GIANT") return (entry.material ?? "").toLowerCase() === "wood" && name === "giantart";
  return name === "matted and framed" || name === "canvas framed" || name === "gallery wrap";
}

/**
 * Picks the product categories (Giant art, Canvas framed...) whose products of
 * the selected artworks go in an export: a marketplace feed (with its feed
 * type) or the plain product list (without).
 */
export function FeedExportDialog({
  open,
  onClose,
  onConfirm,
  products = false,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (categoryIds: number[], feedType: FeedType) => void | Promise<void>;
  /** The product list: no feed type. */
  products?: boolean;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const [feedType, setFeedType] = useState<FeedType>("BISON_OFFICE");
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const categories = useQuery({
    queryKey: ["product", "category", "open"],
    queryFn: () => api.productCategories.getWithOpenProducts(),
    enabled: open,
  });

  const eligible = useMemo(() => categories.data ?? [], [categories.data]);

  // Preselected once per opening, so clearing the list does not tick it all again.
  const [preselected, setPreselected] = useState(false);
  useEffect(() => {
    if (!open) {
      setPreselected(false);
      setCategoryIds([]);
    } else if (!preselected && eligible.length > 0) {
      setCategoryIds(eligible.filter(selectedByDefault).map((entry) => String(entry.id)));
      setPreselected(true);
    }
  }, [open, preselected, eligible]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={products ? t("dialog.export.products.title", "Export products") : t("dialog.export.feed.title", "Generate feed")}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={categoryIds.length === 0}
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm(categoryIds.map(Number), feedType);
                onClose();
              } finally {
                setBusy(false);
              }
            }}
          >
            {t("dialog.confirm.export", "Export")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {!products && (
          <Field label={t("form.field.feed-type", "Feed type")} required>
            <Select
              value={feedType}
              options={FEED_TYPES.map((entry) => ({ value: entry, label: FEED_TYPE_LABELS[entry] }))}
              onChange={(event) => setFeedType(event.target.value as FeedType)}
            />
          </Field>
        )}
        <Field label={t("form.field.categories", "Categories")} required>
          <MultiSelect
            options={eligible.map((entry) => ({ value: String(entry.id), label: categoryLabel(entry) }))}
            value={categoryIds}
            onChange={setCategoryIds}
          />
        </Field>
      </div>
    </Dialog>
  );
}

/** Picks a display collection to add the selected artwork images to. */
export function DisplayCollectionPickerDialog({
  open,
  onClose,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (collectionId: number, name: string) => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const [collectionId, setCollectionId] = useState("");
  const [busy, setBusy] = useState(false);

  const collections = useQuery({
    queryKey: ["display-collection"],
    queryFn: () => api.displayCollections.findAll(),
    enabled: open,
  });

  const chosen = (collections.data ?? []).find((entry) => String(entry.id) === collectionId);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("dialog.add-to-display-collection.title", "Add to display collection")}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!chosen}
            onClick={async () => {
              if (!chosen) return;
              setBusy(true);
              try {
                await onConfirm(chosen.id, chosen.name);
                onClose();
              } finally {
                setBusy(false);
              }
            }}
          >
            {t("dialog.confirm.add", "Add")}
          </Button>
        </>
      }
    >
      <Field label={t("form.field.display-collection", "Display collection")} required>
        <Select
          value={collectionId}
          options={(collections.data ?? []).map((entry) => ({
            value: String(entry.id),
            label: entry.name,
          }))}
          placeholder={t("form.field.select", "Select")}
          onChange={(event) => setCollectionId(event.target.value)}
        />
      </Field>
    </Dialog>
  );
}

/**
 * Renames an artwork's code.
 *
 * The publisher prefix is fixed and shown as a prefix rather than typed, which
 * is what keeps the new code inside the publisher's namespace. The server then
 * renames every Shopify listing; any failures come back as a list.
 */
export function UpdateArtworkCodeDialog({
  open,
  onClose,
  artworkId,
  currentCode,
  publisherPrefix,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  artworkId: number;
  currentCode: string;
  publisherPrefix: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const prefix = publisherPrefix.toUpperCase();
  const initial = currentCode.startsWith(prefix) ? currentCode.slice(prefix.length) : currentCode;
  const [suffix, setSuffix] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [failures, setFailures] = useState<string[] | null>(null);

  useEffect(() => {
    if (open) setSuffix(initial);
  }, [initial, open]);

  const full = `${prefix}${suffix.toUpperCase()}`;
  const unchanged = full === currentCode;

  const submit = async () => {
    setBusy(true);
    try {
      const existing = await api.artworks.getByCode(full);
      if (existing && existing.id !== artworkId) {
        toast.error(t("form.validation.field.unique", "Value already exists"));
        return;
      }
      const result = await api.artworks.updateCode(artworkId, full);
      if (result.length === 0) {
        toast.success(t("notification.update.artwork.code.success", "Artwork code updated successfully"));
        onDone();
        onClose();
      } else {
        setFailures(result);
        onDone();
      }
    } catch (failure) {
      toast.error(
        failure instanceof Error
          ? failure.message
          : t("notification.update.artwork.code.error", "Failed to update artwork code"),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog
        open={open && failures === null}
        onClose={onClose}
        title={t("dialog.update.artwork.code.title", "Update artwork code")}
        actions={
          <>
            <Button variant="secondary" onClick={onClose}>
              {t("dialog.cancel", "Cancel")}
            </Button>
            <Button
              variant="primary"
              loading={busy}
              disabled={!suffix.trim() || unchanged}
              onClick={() => void submit()}
            >
              {t("dialog.confirm.update", "Update")}
            </Button>
          </>
        }
      >
        <Field
          label={t("form.field.code", "Code")}
          required
          hint={`${t("column.code", "Code")}: ${full}`}
        >
          <div className="flex items-center gap-2">
            <Badge variant="neutral">{prefix || "—"}</Badge>
            <Input
              value={suffix}
              onChange={(event) => setSuffix(event.target.value.toUpperCase())}
              className="uppercase"
            />
          </div>
        </Field>
      </Dialog>

      <MessageListDialog
        open={failures !== null}
        onClose={() => {
          setFailures(null);
          onClose();
        }}
        title={t("dialog.update.artwork.code.partial-failures", "Partial failures")}
        messages={failures ?? []}
      />
    </>
  );
}

/** A single-file import that answers with a per-row error report. */
export function ImportFileDialog({
  open,
  onClose,
  title,
  accept,
  onImport,
  onImported,
  extra,
  disabled,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  accept: string;
  onImport: (file: File) => Promise<string[]>;
  onImported: () => void;
  /** Extra fields rendered above the drop zone (e.g. a publisher picker). */
  extra?: React.ReactNode;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[] | null>(null);

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const result = await onImport(file);
      if (result.length === 0) {
        toast.success(t("notification.import.success", "File imported successfully"));
        onImported();
        onClose();
        setFile(null);
      } else {
        setErrors(result);
        onImported();
      }
    } catch (failure) {
      toast.error(
        failure instanceof Error
          ? failure.message
          : t("notification.import.error", "Failed to import file"),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog
        open={open && errors === null}
        onClose={onClose}
        title={title}
        actions={
          <>
            <Button variant="secondary" onClick={onClose}>
              {t("dialog.cancel", "Cancel")}
            </Button>
            <Button
              variant="primary"
              loading={busy}
              disabled={!file || disabled}
              onClick={() => void submit()}
            >
              {t("dialog.confirm.import", "Import")}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {extra}
          <FileDrop
            accept={accept}
            files={file ? [file] : []}
            onRemove={() => setFile(null)}
            onFiles={(files) => setFile(files[0] ?? null)}
          />
        </div>
      </Dialog>

      <MessageListDialog
        open={errors !== null}
        onClose={() => {
          setErrors(null);
          setFile(null);
          onClose();
        }}
        title={t("dialog.import.errors", "Import errors")}
        messages={errors ?? []}
      />
    </>
  );
}

// ── artwork image upload ──────────────────────────────────────────────────

export interface UploadRow {
  file: File;
  catalogId: number;
  publisherId: number;
  publisherCode: string;
  code: string;
  aspectRatioStrategy: AspectRatioStrategy;
  dpi: number;
  ratio: Ratio;
  imageWidth: number;
  imageHeight: number;
}

/**
 * Picks the enum ratio closest to the image's own aspect, mirroring
 * `ArtworkService.closestRatio`. CUSTOM is never a candidate — it is the
 * fallback when the dimensions are unknown.
 */
export function closestRatio(width: number, height: number): Ratio {
  if (width <= 0 || height <= 0) return "CUSTOM";
  const target = width >= height ? width / height : height / width;
  let best: Ratio = "CUSTOM";
  let bestDiff = Number.MAX_VALUE;
  for (const ratio of RATIOS) {
    if (ratio === "CUSTOM") continue;
    const diff = Math.abs(RATIO_VALUES[ratio] - target);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = ratio;
    }
  }
  return best;
}

/**
 * Reads an image's pixel dimensions in the browser.
 *
 * TIFFs are accepted by the upload but no browser decodes them, so those come
 * back as 0×0 — the row then shows a blank max-print size and a CUSTOM ratio
 * until the user sets one, which is the same choice the operator would make.
 */
async function readDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
      URL.revokeObjectURL(url);
    };
    image.onerror = () => {
      resolve({ width: 0, height: 0 });
      URL.revokeObjectURL(url);
    };
    image.src = url;
  });
}

/**
 * Batch artwork upload.
 *
 * Each file becomes a row whose code defaults to (publisher prefix + filename
 * without extension) and whose ratio is auto-detected; the global publisher
 * picker rewrites every row's prefix at once, which is how a whole delivery
 * from one publisher is prepared in a couple of clicks.
 */
export function AddArtworkImagesDialog({
  open,
  onClose,
  onUploaded,
}: {
  open: boolean;
  onClose: () => void;
  onUploaded: () => void;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const [rows, setRows] = useState<UploadRow[]>([]);
  const [globalPublisher, setGlobalPublisher] = useState("");
  const [busy, setBusy] = useState(false);

  const catalogs = useQuery({
    queryKey: ["catalog"],
    queryFn: () => api.catalogs.findAll(),
    enabled: open,
  });
  const publishers = useQuery({
    queryKey: ["publisher"],
    queryFn: () => api.publishers.findAll(),
    enabled: open,
  });
  const dpi = useQuery({
    queryKey: ["global-dpi"],
    queryFn: () => api.globalDpi.get(),
    enabled: open,
  });

  const globalDpi = dpi.data?.dpi ?? 100;
  const catalogList = catalogs.data ?? [];
  const publisherList = publishers.data ?? [];

  const defaultCatalog =
    catalogList.find((entry) => entry.name.toLowerCase() === "clicart") ?? catalogList[0];
  const defaultPublisher =
    publisherList.find((entry) => entry.code === "CT") ?? publisherList[0];

  const addFiles = async (files: File[]) => {
    const next: UploadRow[] = [];
    for (const file of files) {
      const { width, height } = await readDimensions(file);
      const base = file.name.replace(/\.[^.]*$/, "");
      next.push({
        file,
        catalogId: defaultCatalog?.id ?? 0,
        publisherId: defaultPublisher?.id ?? 0,
        publisherCode: base,
        code: `${(defaultPublisher?.code ?? "").toUpperCase()}${base}`,
        aspectRatioStrategy: "RESIZE",
        dpi: globalDpi,
        ratio: closestRatio(width, height),
        imageWidth: width,
        imageHeight: height,
      });
    }
    setRows((current) => [...current, ...next]);
  };

  const applyGlobalPublisher = (publisherId: string) => {
    setGlobalPublisher(publisherId);
    const publisher = publisherList.find((entry) => String(entry.id) === publisherId);
    if (!publisher) return;
    const prefix = (publisher.code ?? "").toUpperCase();
    setRows((current) =>
      current.map((row) => ({
        ...row,
        publisherId: publisher.id,
        code: `${prefix}${row.publisherCode}`,
      })),
    );
  };

  const reset = () => {
    setRows((current) =>
      current.map((row) => {
        const publisher = publisherList.find((entry) => entry.id === row.publisherId);
        const prefix = (publisher?.code ?? "").toUpperCase();
        const base = row.file.name.replace(/\.[^.]*$/, "");
        return {
          ...row,
          publisherCode: base,
          code: `${prefix}${base}`,
          dpi: globalDpi,
          ratio: closestRatio(row.imageWidth, row.imageHeight),
        };
      }),
    );
  };

  const update = (index: number, changes: Partial<UploadRow>) =>
    setRows((current) => current.map((row, i) => (i === index ? { ...row, ...changes } : row)));

  const submit = async () => {
    setBusy(true);
    let failed = 0;
    for (const row of rows) {
      try {
        await api.artworks.add({
          catalog_id: row.catalogId,
          publisher_id: row.publisherId,
          publisher_code: row.publisherCode,
          code: row.code,
          aspect_ratio_strategy: row.aspectRatioStrategy,
          dpi: row.dpi,
          ratio: row.ratio === "CUSTOM" ? null : row.ratio,
          file: row.file,
        });
      } catch {
        failed += 1;
        toast.error(`${t("notification.upload.error", "Failed to upload")}: ${row.code}`);
      }
    }
    setBusy(false);
    if (failed === 0) toast.success(t("notification.add.started", "Image upload started"));
    setRows([]);
    onUploaded();
    onClose();
  };

  const maxPrint = (row: UploadRow) =>
    row.dpi > 0 && row.imageWidth > 0 && row.imageHeight > 0
      ? `${Math.floor(row.imageWidth / row.dpi)} x ${Math.floor(row.imageHeight / row.dpi)} in`
      : "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("dialog.add-images.title", "Add images")}
      size="lg"
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={rows.length === 0}
            onClick={() => void submit()}
          >
            {t("dialog.confirm.add", "Add")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <FileDrop
          accept=".jpg,.jpeg,.tif,.tiff,image/jpeg,image/tiff"
          multiple
          onFiles={(files) => void addFiles(files)}
        />

        {rows.length > 0 && (
          <>
            <div className="flex items-end gap-2">
              <Field label={t("form.field.publisher", "Publisher")} className="flex-1">
                <Select
                  value={globalPublisher}
                  options={publisherList.map((entry) => ({
                    value: String(entry.id),
                    label: entry.name,
                  }))}
                  placeholder={t("form.field.select", "Select")}
                  onChange={(event) => applyGlobalPublisher(event.target.value)}
                />
              </Field>
              <Button variant="ghost" onClick={reset}>
                {t("button.reset", "Reset")}
              </Button>
            </div>

            <div className="overflow-x-auto rounded border border-line">
              <table className="w-full text-[12px]">
                <thead className="bg-surface-sunk text-[11px] uppercase tracking-wider text-ink-faint">
                  <tr>
                    <th className="px-2 py-1 text-left">{t("column.catalog", "Catalog")}</th>
                    <th className="px-2 py-1 text-left">{t("column.publisher", "Publisher")}</th>
                    <th className="px-2 py-1 text-left">{t("column.code", "Code")}</th>
                    <th className="px-2 py-1 text-right">{t("column.dpi", "DPI")}</th>
                    <th className="px-2 py-1 text-left">{t("column.ratio", "Ratio")}</th>
                    <th className="px-2 py-1 text-left">
                      {t("column.max-print-size", "Max print size")}
                    </th>
                    <th className="px-2 py-1 text-left">
                      {t("column.aspect-ratio-strategy", "Aspect ratio strategy")}
                    </th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => {
                    const prefix = (
                      publisherList.find((entry) => entry.id === row.publisherId)?.code ?? ""
                    ).toUpperCase();
                    return (
                      <tr key={`${row.file.name}-${index}`} className="border-t border-line-soft">
                        <td className="px-2 py-1">
                          <Select
                            className="h-7 text-[12px]"
                            value={String(row.catalogId)}
                            options={catalogList.map((entry) => ({
                              value: String(entry.id),
                              label: entry.name,
                            }))}
                            onChange={(event) =>
                              update(index, { catalogId: Number(event.target.value) })
                            }
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Select
                            className="h-7 text-[12px]"
                            value={String(row.publisherId)}
                            options={publisherList.map((entry) => ({
                              value: String(entry.id),
                              label: entry.name,
                            }))}
                            onChange={(event) => {
                              const publisher = publisherList.find(
                                (entry) => String(entry.id) === event.target.value,
                              );
                              update(index, {
                                publisherId: Number(event.target.value),
                                code: `${(publisher?.code ?? "").toUpperCase()}${row.publisherCode}`,
                              });
                            }}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <div className="flex items-center gap-1">
                            <span className="text-ink-faint">{prefix}</span>
                            <Input
                              className="h-7 text-[12px] uppercase"
                              value={row.publisherCode}
                              onChange={(event) => {
                                const value = event.target.value.toUpperCase();
                                update(index, {
                                  publisherCode: value,
                                  code: `${prefix}${value}`,
                                });
                              }}
                            />
                          </div>
                        </td>
                        <td className="px-2 py-1">
                          <Input
                            className="h-7 w-20 text-right text-[12px]"
                            type="number"
                            min={globalDpi}
                            step={5}
                            value={row.dpi}
                            onChange={(event) => update(index, { dpi: Number(event.target.value) })}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Select
                            className="h-7 text-[12px]"
                            value={row.ratio}
                            options={RATIOS.map((entry) => ({ value: entry, label: entry }))}
                            onChange={(event) =>
                              update(index, { ratio: event.target.value as Ratio })
                            }
                          />
                        </td>
                        <td className="px-2 py-1 text-ink-muted">{maxPrint(row)}</td>
                        <td className="px-2 py-1">
                          <Select
                            className="h-7 text-[12px]"
                            value={row.aspectRatioStrategy}
                            options={ASPECT_RATIO_STRATEGIES.map((entry) => ({
                              value: entry,
                              label: entry,
                            }))}
                            onChange={(event) =>
                              update(index, {
                                aspectRatioStrategy: event.target.value as AspectRatioStrategy,
                              })
                            }
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="size-6 p-0"
                            onClick={() =>
                              setRows((current) => current.filter((_, i) => i !== index))
                            }
                          >
                            ×
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}
